// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// feature-components
import EnergyRichTreeView from '@/features/energyxplorer/components/EnergyRichTreeView';

// feature-utils
import {
  getChildren,
  getRootNode,
} from '@/features/energyxplorer/utils/energyTreeFunctions';
import {
  clampTimestampsSummary,
  isOutOfBounds,
} from '@/features/energyxplorer/utils/timestampsSummaryHelpers';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import { useTimestampSummaryEvaluation } from './useTimestampSummaryEvaluation';

type WarningVariant = 'node' | 'seu';

type EnergyTreeNode = {
  energyTreeNodeId: string | number;
  parentId: string | number | null;
  type?: string | null;
  name?: string;
  label?: string;
  energyGroupId?: string | number;
};

type WarningRendererProps = {
  variant: WarningVariant;
  energyGroup?: {
    energyGroupId?: string | number;
    energyTreeNodes?: EnergyTreeNode[];
  } | null;
  node?: EnergyTreeNode | null;
  seu?: string;
  energyGroupId?: string | number;
  includeChildrenInWarning?: boolean;
  includeAllChildrenInWarning?: boolean;
};

function toTreeNodeLite(treeNodes: EnergyTreeNode[] = []) {
  return treeNodes.map((treeNode) => ({
    energyTreeNodeId: String(treeNode.energyTreeNodeId),
    parentId: treeNode.parentId === null ? null : String(treeNode.parentId),
    type: treeNode.type,
  }));
}

export default function TimestampsSummaryWarningRenderer({
  variant,
  energyGroup,
  node,
  seu,
  energyGroupId,
  includeChildrenInWarning = false,
  includeAllChildrenInWarning = false,
}: WarningRendererProps) {
  const { t } = useTranslation('energyxplorer');
  const { timeRange, setRange } = useUrlDateRange();
  const { accountId } = useParams({ strict: false });
  const [isOpen, setIsOpen] = useState(false);

  const accountIdNumber = Number(accountId);
  const resolvedEnergyGroupId = Number(
    variant === 'node' ? node?.energyGroupId : energyGroupId,
  );

  const { data: fetchedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: resolvedEnergyGroupId },
    {
      skip:
        !Number.isFinite(resolvedEnergyGroupId) ||
        !Number.isFinite(accountIdNumber),
    },
  );

  const effectiveEnergyGroup = fetchedEnergyGroup ?? energyGroup;
  const treeNodes = effectiveEnergyGroup?.energyTreeNodes || [];

  const resolvedNode =
    node ??
    (variant === 'node' && treeNodes.length > 0
      ? getRootNode(treeNodes)
      : null);

  const {
    displayWarning,
    adjustedRange,
    isSummaryInverted,
    isSummaryNoData,
    summaryMap,
  } = useTimestampSummaryEvaluation({
    accountId: Number.isFinite(accountIdNumber) ? accountIdNumber : null,
    energyGroupId: Number.isFinite(resolvedEnergyGroupId)
      ? resolvedEnergyGroupId
      : null,
    scope: variant,
    compareRange: timeRange,
    treeNodes: toTreeNodeLite(treeNodes),
    nodeId:
      variant === 'node' && resolvedNode?.energyTreeNodeId
        ? String(resolvedNode.energyTreeNodeId)
        : null,
    seuType: variant === 'seu' ? seu : null,
    includeChildrenInWarning,
    includeAllChildrenInWarning,
  });

  const adjustedRangeString = adjustedRange
    ? `${adjustedRange.start.setLocale('da').toFormat('dd/MM/yyyy')} - ${adjustedRange.end.setLocale('da').toFormat('dd/MM/yyyy')}`
    : null;

  const adjustedRangeMessage = isSummaryNoData
    ? t('common.timestamp_summary_no_valid_period')
    : adjustedRangeString
      ? t('common.timestamp_summary_warning_with_range', {
          timeRange: adjustedRangeString,
        })
      : null;

  let detailedSummary:
    | Record<string, unknown>
    | Record<string, unknown>[]
    | null = null;

  if (
    variant === 'node' &&
    resolvedNode?.energyTreeNodeId &&
    treeNodes.length
  ) {
    const buildNodeTree = (
      currentNode: EnergyTreeNode,
      allNodes: EnergyTreeNode[],
    ) => {
      const children = getChildren(currentNode.energyTreeNodeId, allNodes);
      const nodeSummary =
        summaryMap[String(currentNode.energyTreeNodeId)] || null;

      return {
        id: currentNode.energyTreeNodeId,
        label:
          currentNode.name ||
          currentNode.label ||
          `Node ${currentNode.energyTreeNodeId}`,
        children: children.length
          ? children.map((child: EnergyTreeNode) =>
              buildNodeTree(child, allNodes),
            )
          : undefined,
        isOutOfBounds: !!nodeSummary && isOutOfBounds(nodeSummary, timeRange),
        ...currentNode,
        energySummary: {
          TimestampsSummary: clampTimestampsSummary(nodeSummary),
        },
      };
    };

    const nodeToAnalyze = treeNodes.find(
      (treeNode) =>
        String(treeNode.energyTreeNodeId) ===
        String(resolvedNode.energyTreeNodeId),
    );

    if (nodeToAnalyze) {
      detailedSummary = buildNodeTree(nodeToAnalyze, treeNodes);
    }
  }

  if (variant === 'seu') {
    detailedSummary = treeNodes
      .filter((treeNode) => treeNode.type === seu)
      .map((treeNode) => {
        const nodeSummary =
          summaryMap[String(treeNode.energyTreeNodeId)] || null;

        return {
          id: treeNode.energyTreeNodeId,
          label: treeNode.name,
          children: [],
          ...treeNode,
          isOutOfBounds: !!nodeSummary && isOutOfBounds(nodeSummary, timeRange),
          energySummary: {
            TimestampsSummary: clampTimestampsSummary(nodeSummary),
          },
        };
      });
  }

  if (!displayWarning) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={
          <>
            <Box whiteSpace={'pre-line'}>
              {t('common.timestamp_summary_warning')}
            </Box>
          </>
        }
      >
        <Button
          onClick={() => setIsOpen(true)}
          variant="outlined"
          startIcon={
            <Badge
              overlap="circular"
              badgeContent={
                <WarningRoundedIcon fontSize="small" color="warning" />
              }
            >
              <CalendarTodayRoundedIcon fontSize="small" />
            </Badge>
          }
        >
          {t('actions.details', { ns: 'common', defaultValue: 'Details' })}
        </Button>
      </Tooltip>

      <Dialog maxWidth="lg" open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>{t('data_overview.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1">
            {t('data_overview.subtitle')}
          </Typography>
          {adjustedRangeMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Box whiteSpace={'pre-line'}>{adjustedRangeMessage}</Box>
            </Typography>
          )}
          {variant === 'node' &&
            detailedSummary &&
            !Array.isArray(detailedSummary) && (
              <EnergyRichTreeView
                items={[detailedSummary]}
                defaultExpanded={[
                  detailedSummary.energyTreeNodeId as string | number,
                ]}
                selectedId={undefined}
                onSelect={undefined}
              />
            )}
          {variant === 'seu' &&
            Array.isArray(detailedSummary) &&
            detailedSummary.length > 0 && (
              <EnergyRichTreeView
                items={detailedSummary}
                defaultExpanded={[detailedSummary[0].energyTreeNodeId]}
                selectedId={undefined}
                onSelect={undefined}
              />
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOpen(false)} variant="text">
            {t('actions.close', { ns: 'common' })}
          </Button>
          {!isSummaryInverted && !isSummaryNoData && (
            <Button
              variant="outlined"
              onClick={() => {
                if (!adjustedRange) return;

                const { start: adjustedStart, end: adjustedEnd } =
                  adjustedRange;
                if (!adjustedStart || !adjustedEnd) return;

                setRange(adjustedStart, adjustedEnd, {
                  // Keep exact evaluated bounds; day rounding can re-introduce out-of-bounds.
                  roundToDay: false,
                  clampToNow: true,
                });

                setIsOpen(false);
              }}
            >
              {t('daterange.adjust_period', { ns: 'energyxplorer' })}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
